/**
 * FlightMelBadge Component
 * Small badge showing MEL status for trip/flight cards
 */

import React from 'react';
import { cn } from '@/utils/cn';
import { Link } from 'react-router-dom';
import type { ActiveMel } from '@/types/mel';
import melService from '@/services/melService';
import {
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';

interface FlightMelBadgeProps {
  mels: ActiveMel[];
  aircraftId?: number;
  variant?: 'dot' | 'badge' | 'detailed';
  showLink?: boolean;
  className?: string;
}

export const FlightMelBadge: React.FC<FlightMelBadgeProps> = ({
  mels,
  aircraftId,
  variant = 'badge',
  showLink = false,
  className,
}) => {
  if (mels.length === 0) return null;

  // Check for expired MELs
  const hasExpired = mels.some((m) => melService.isExpired(m.expiresAt));
  const hasExpiringSoon = mels.some((m) => melService.isExpiringSoon(m.expiresAt));

  const getColors = () => {
    if (hasExpired) {
      return {
        bg: 'bg-red-100 dark:bg-red-900/30',
        text: 'text-red-700 dark:text-red-400',
        border: 'border-red-300 dark:border-red-700',
        dot: 'bg-red-500',
      };
    }
    if (hasExpiringSoon) {
      return {
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        text: 'text-yellow-700 dark:text-yellow-400',
        border: 'border-yellow-300 dark:border-yellow-700',
        dot: 'bg-yellow-500',
      };
    }
    return {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-700 dark:text-blue-400',
      border: 'border-blue-300 dark:border-blue-700',
      dot: 'bg-blue-500',
    };
  };

  const colors = getColors();

  const getIcon = () => {
    if (hasExpired) return <XCircleIcon className="w-3 h-3" />;
    if (hasExpiringSoon) return <ExclamationTriangleIcon className="w-3 h-3" />;
    return <WrenchScrewdriverIcon className="w-3 h-3" />;
  };

  // Dot variant - minimal indicator
  if (variant === 'dot') {
    const badge = (
      <span
        className={cn(
          'relative inline-block w-2.5 h-2.5 rounded-full',
          colors.dot,
          className
        )}
        title={`${mels.length} active MEL${mels.length !== 1 ? 's' : ''}`}
      >
        {/* Pulse animation for warnings */}
        {(hasExpired || hasExpiringSoon) && (
          <span
            className={cn(
              'absolute inset-0 rounded-full animate-ping opacity-75',
              colors.dot
            )}
          />
        )}
      </span>
    );

    if (showLink && aircraftId) {
      return (
        <Link to={`/mel/active?aircraft=${aircraftId}`} className="inline-block">
          {badge}
        </Link>
      );
    }

    return badge;
  }

  // Badge variant - compact count
  if (variant === 'badge') {
    const badge = (
      <span
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border',
          colors.bg,
          colors.text,
          colors.border,
          className
        )}
        title={`${mels.length} active MEL${mels.length !== 1 ? 's' : ''}`}
      >
        {getIcon()}
        <span>{mels.length} MEL</span>
      </span>
    );

    if (showLink && aircraftId) {
      return (
        <Link
          to={`/mel/active?aircraft=${aircraftId}`}
          className="hover:opacity-80 transition-opacity"
        >
          {badge}
        </Link>
      );
    }

    return badge;
  }

  // Detailed variant - shows item numbers
  const detailedContent = (
    <div
      className={cn(
        'inline-flex flex-col gap-1 p-2 rounded-lg border',
        colors.bg,
        colors.border,
        className
      )}
    >
      <div className={cn('flex items-center gap-1.5 text-xs font-medium', colors.text)}>
        {getIcon()}
        <span>
          {mels.length} Active MEL{mels.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {mels.slice(0, 3).map((mel) => (
          <span
            key={mel.id}
            className={cn(
              'px-1.5 py-0.5 rounded text-[10px] font-mono font-medium',
              'bg-background/50 text-foreground'
            )}
          >
            {mel.melItem.itemNumber}
          </span>
        ))}
        {mels.length > 3 && (
          <span className={cn('text-[10px]', colors.text)}>+{mels.length - 3}</span>
        )}
      </div>
    </div>
  );

  if (showLink && aircraftId) {
    return (
      <Link
        to={`/mel/active?aircraft=${aircraftId}`}
        className="hover:opacity-80 transition-opacity"
      >
        {detailedContent}
      </Link>
    );
  }

  return detailedContent;
};

/**
 * Simple MEL indicator for inline use
 */
export const MelIndicator: React.FC<{
  count: number;
  hasWarning?: boolean;
  className?: string;
}> = ({ count, hasWarning = false, className }) => {
  if (count === 0) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs',
        hasWarning
          ? 'text-yellow-600 dark:text-yellow-400'
          : 'text-foreground/60',
        className
      )}
    >
      <WrenchScrewdriverIcon className="w-3 h-3" />
      {count}
    </span>
  );
};

export default FlightMelBadge;
